import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  Grid,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  DashboardRounded,
  LogoutRounded,
  MenuRounded,
  PersonRounded,
} from "@mui/icons-material";
import { alpha, styled } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import companyLogo from "../../assets/nav-lexmom.webp";
import { logout } from "../../redux/Apps/users/userSlice";
import Wormhole from "../Wormhole";
import UserAvatar from "../UserAvatar";

export const practiceAreas = [
  "Business Law",
  "Criminal Defense",
  "Family Law",
  "Personal Injury",
  "Real Estate",
  "Estate Planning",
  "Immigration",
  "Employment Law",
  "Intellectual Property",
  "Bankruptcy",
  "Tax Law",
  "Civil Litigation",
];

const navItems = [
  { label: "Home", id: "home", path: "/", type: "navigate" },
  { label: "Ai Team", id: "ai_team", path: "/ai-team", type: "navigate" },
  { label: "About", id: "about-us", path: "/about-us", type: "navigate" },
  {
    label: "Testimonials",
    id: "testimonials",
    type: "scroll",
    section: "#testimonials",
  },
  { label: "FAQ", id: "faq", type: "scroll", section: "#faq" },
  { label: "Pricing", id: "pricing", path: "/pricing", type: "navigate" },
];

const dropdownTransition = {
  duration: 0.24,
  ease: [0.22, 1, 0.36, 1],
};

const mobilePanelTransition = {
  type: "spring",
  stiffness: 280,
  damping: 28,
};

const mobileListVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.08,
    },
  },
};

const mobileItemVariants = {
  hidden: { opacity: 0, x: 18 },
  visible: {
    opacity: 1,
    x: 0,
    transition: dropdownTransition,
  },
};

const NavLink = styled("button", {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  position: "relative",
  border: "none",
  outline: "none",
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  background: active ? alpha(theme.palette.primary.main, 0.12) : "transparent",
  fontWeight: active ? 600 : 500,
  fontSize: "0.88rem",
  lineHeight: 1,
  padding: "0.5rem 0.75rem",
  borderRadius: "999px",
  transition: "all 0.24s ease",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.fontFamily,
  boxShadow: active
    ? `0 10px 30px ${alpha(theme.palette.primary.main, 0.12)}`
    : "none",
  "&:hover": {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: "translateY(-1px)",
  },
}));

const GradientButton = styled(Button)(() => ({
  background: "linear-gradient(135deg, #472ed1 0%, #6a4ee6 100%)",
  color: "#fff",
  fontWeight: 600,
  textTransform: "none",
  fontSize: "0.86rem",
  borderRadius: "999px",
  paddingInline: "1.15rem",
  minHeight: "2.9rem",
  boxShadow: `0 16px 40px ${alpha("#472ed1", 0.24)}`,
  "&:hover": {
    background: "linear-gradient(135deg, #6a4ee6 0%, #472ed1 100%)",
    boxShadow: `0 18px 42px ${alpha("#472ed1", 0.3)}`,
    transform: "translateY(-1px)",
  },
}));

const OutlinedButton = styled(Button)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 600,
  textTransform: "none",
  fontSize: "0.86rem",
  borderRadius: "999px",
  paddingInline: "1.1rem",
  minHeight: "2.9rem",
  borderColor: alpha(theme.palette.primary.main, 0.2),
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    borderColor: alpha(theme.palette.primary.main, 0.28),
    color: theme.palette.primary.main,
    transform: "translateY(-1px)",
  },
}));

const DropdownButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  backgroundColor: active
    ? alpha(theme.palette.primary.main, 0.12)
    : "transparent",
  fontWeight: active ? 600 : 500,
  fontSize: "0.88rem",
  minHeight: "2.8rem",
  textTransform: "none",
  padding: "0.72rem 1rem",
  borderRadius: "999px",
  boxShadow: active
    ? `0 10px 30px ${alpha(theme.palette.primary.main, 0.12)}`
    : "none",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
  },
}));

const parseStoredUser = (value) => {
  try {
    const parsedValue = JSON.parse(value || "null");
    return parsedValue && typeof parsedValue === "object" ? parsedValue : {};
  } catch {
    return {};
  }
};

const getActiveNavId = (pathname, hash) => {
  const normalizedPathname = pathname === "/ai_team" ? "/ai-team" : pathname;

  if (normalizedPathname === "/" && hash) {
    const sectionMatch = navItems.find(
      (item) => item.type === "scroll" && item.section === hash,
    );
    if (sectionMatch) return sectionMatch.id;
  }

  const routeMatch = navItems.find(
    (item) => item.type === "navigate" && item.path === normalizedPathname,
  );
  if (routeMatch) return routeMatch.id;

  if (normalizedPathname === "/") return "home";

  return "";
};

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { token, user } = useSelector((state) => state.user);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [practiceAreasAnchor, setPracticeAreasAnchor] = useState(null);
  const [practiceAreasOpen, setPracticeAreasOpen] = useState(false);
  const [mobilePracticeAreasOpen, setMobilePracticeAreasOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const practiceAreasTimerRef = useRef(null);

  const loginUserData = useMemo(() => {
    const storedUser = parseStoredUser(localStorage.getItem("userData"));
    return Object.keys(storedUser).length ? storedUser : user || {};
  }, [user]);

  const activeLink = useMemo(
    () => getActiveNavId(location.pathname, location.hash),
    [location.pathname, location.hash],
  );

  const userDisplayName = useMemo(() => {
    if (loginUserData?.first_name || loginUserData?.last_name) {
      return `${loginUserData.first_name || ""} ${loginUserData.last_name || ""}`.trim();
    }

    return (
      loginUserData?.reminder_mail ||
      loginUserData?.email ||
      user?.email ||
      "User"
    );
  }, [loginUserData, user]);

  const userSecondaryText =
    loginUserData?.email || loginUserData?.reminder_mail || user?.email || "";

  const clearPracticeAreasTimer = () => {
    if (practiceAreasTimerRef.current) {
      window.clearTimeout(practiceAreasTimerRef.current);
      practiceAreasTimerRef.current = null;
    }
  };

  const openPracticeAreasMenu = (anchorElement) => {
    clearPracticeAreasTimer();
    setPracticeAreasAnchor(anchorElement);
    setPracticeAreasOpen(true);
  };

  const closePracticeAreasMenu = () => {
    clearPracticeAreasTimer();
    setPracticeAreasOpen(false);
  };

  const schedulePracticeAreasClose = () => {
    clearPracticeAreasTimer();
    practiceAreasTimerRef.current = window.setTimeout(() => {
      setPracticeAreasOpen(false);
    }, 120);
  };

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        window.setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location]);

  useEffect(() => () => clearPracticeAreasTimer(), []);

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false);
      setMobilePracticeAreasOpen(false);
    } else {
      clearPracticeAreasTimer();
      setPracticeAreasOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
    setMobilePracticeAreasOpen(false);
    closePracticeAreasMenu();
  };

  const handleLinkClick = (item) => {
    localStorage.removeItem("from_start_trial");
    window.dispatchEvent(new Event("from_start_trial"));
    closeAllMenus();

    if (item.type === "navigate") {
      navigate(item.path);
      return;
    }

    if (location.pathname === "/" && location.hash === item.section) {
      const element = document.querySelector(item.section);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    navigate({ pathname: "/", hash: item.section });
  };

  const handlePracticeAreaClick = () => {
    closeAllMenus();
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
    handleCloseUserMenu();
    closeAllMenus();
  };

  const renderPracticeAreasGrid = (isCompact = false) => (
    <Box
      sx={{
        display: "grid",
        gap: isCompact ? "0.55rem" : "0.8rem",
        gridTemplateColumns: isCompact
          ? { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }
          : "repeat(2, minmax(0, 1fr))",
      }}
    >
      {practiceAreas.map((area) => (
        <Button
          key={area}
          fullWidth
          onClick={() => handlePracticeAreaClick(area)}
          sx={{
            justifyContent: "flex-start",
            textTransform: "none",
            color: theme.palette.text.primary,
            fontSize: isCompact ? "0.8rem" : "0.88rem",
            fontWeight: 500,
            padding: isCompact ? "0.72rem 0.85rem" : "0.8rem 0.95rem",
            borderRadius: "1rem",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              transform: "translateY(-1px)",
            },
          }}
        >
          {area}
        </Button>
      ))}
    </Box>
  );

  const renderUserActions = (isMobileView = false) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobileView ? "column" : "row",
        gap: isMobileView ? "0.85rem" : "0.8rem",
        alignItems: isMobileView ? "stretch" : "center",
      }}
    >
      <OutlinedButton
        variant="outlined"
        fullWidth={isMobileView}
        startIcon={<CalendarMonthRoundedIcon fontSize="small" />}
        onClick={() => {
          setMobileMenuOpen(false);
          navigate("/start-trial");
        }}
        id="pricing-toggle-section"
      >
        Start Free Trial
      </OutlinedButton>

      <GradientButton
        fullWidth={isMobileView}
        onClick={() => {
          setMobileMenuOpen(false);
          navigate("/login");
        }}
        endIcon={<LoginRoundedIcon fontSize="small" />}
      >
        Sign In / Up
      </GradientButton>
    </Box>
  );

  const renderAuthenticatedActions = (isMobileView = false) => {
    if (isMobileView) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.85rem",
              p: "0.95rem",
              borderRadius: "1.1rem",
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}
          >
            <UserAvatar
              firstName={userDisplayName}
              imgSrc={loginUserData?.avatar || user?.avatar}
              height="2.8rem"
              width="2.8rem"
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap variant="body2" sx={{ fontWeight: 700 }}>
                {userDisplayName}
              </Typography>
              <Typography
                noWrap
                variant="caption"
                sx={{ color: theme.palette.text.secondary, display: "block" }}
              >
                {userSecondaryText}
              </Typography>
            </Box>
          </Box>

          <OutlinedButton
            fullWidth
            variant="outlined"
            startIcon={<DashboardRounded />}
            onClick={() => {
              setMobileMenuOpen(false);
              navigate("/dashboard");
            }}
          >
            Dashboard
          </OutlinedButton>

          <OutlinedButton
            fullWidth
            variant="outlined"
            startIcon={<PersonRounded />}
            onClick={() => {
              setMobileMenuOpen(false);
              navigate("/dashboard/profile");
            }}
          >
            Profile
          </OutlinedButton>

          <GradientButton
            fullWidth
            startIcon={<LogoutRounded />}
            onClick={handleLogout}
            sx={{
              background: "linear-gradient(135deg, #db3a3e 0%, #f45c61 100%)",
              boxShadow: `0 16px 40px ${alpha("#db3a3e", 0.22)}`,
              "&:hover": {
                background: "linear-gradient(135deg, #f45c61 0%, #db3a3e 100%)",
              },
            }}
          >
            Logout
          </GradientButton>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Tooltip title="Open settings">
          <IconButton
            onClick={handleOpenUserMenu}
            sx={{
              p: 0,
              width: "2.9rem",
              height: "2.9rem",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <UserAvatar
              firstName={userDisplayName}
              imgSrc={loginUserData?.avatar || user?.avatar}
              height="2.5rem"
              width="2.5rem"
            />
          </IconButton>
        </Tooltip>

        <Menu
          sx={{ mt: "46px" }}
          id="menu-appbar"
          anchorEl={anchorElUser}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: "1rem",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                boxShadow: `0 24px 60px ${alpha(theme.palette.common.black, 0.12)}`,
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              navigate("/dashboard");
              handleCloseUserMenu();
            }}
          >
            <ListItemIcon>
              <DashboardRounded fontSize="small" />
            </ListItemIcon>
            <Typography>Dashboard</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              navigate("/dashboard/profile");
              handleCloseUserMenu();
            }}
          >
            <ListItemIcon>
              <PersonRounded fontSize="small" />
            </ListItemIcon>
            <Typography>Profile</Typography>
          </MenuItem>

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutRounded fontSize="small" />
            </ListItemIcon>
            <Typography>Logout</Typography>
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          flexGrow: 1,
          width: "min(100%, 82rem)",
          mx: "auto",
          zIndex: 5,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          p: { xs: "0.4rem", md: "0.5rem" },
          borderRadius: { xs: "1.5rem", md: "5rem" },
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(244,241,255,0.82) 100%)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow: `0 24px 70px ${alpha(theme.palette.common.black, 0.1)}`,
          overflow: "visible",
          fontFamily: theme.typography.fontFamily,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem",
            px: "0.5rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              gap: { xs: "0.75rem", md: "1.5rem" },
            }}
          >
            <Box
              sx={{
                width: { xs: "6rem", md: "7rem" },
                height: { xs: "2.2rem", md: "2.5rem" },
                cursor: "pointer",
                flexShrink: 0,
              }}
              onClick={() => navigate("/")}
            >
              <img
                src={companyLogo}
                alt="LexMom logo"
                style={{ height: "100%", width: "100%", objectFit: "contain" }}
                onError={(event) => {
                  event.target.src = require("../../assets/nav-lexmom.webp");
                }}
              />
            </Box>

            {isMobile ? (
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open navigation"
                sx={{
                  color: theme.palette.text.primary,
                  width: "2.9rem",
                  height: "2.9rem",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                }}
              >
                <MenuRounded />
              </IconButton>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flex: 1,
                  gap: "1rem",
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                    p: "0.3rem",
                    borderRadius: "999px",
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    overflow: "visible",
                  }}
                >
                  {navItems.map((item) => (
                    <NavLink
                      key={item.id}
                      type="button"
                      active={activeLink === item.id ? 1 : 0}
                      onClick={() => handleLinkClick(item)}
                    >
                      {item.label}
                    </NavLink>
                  ))}

                  <Box
                    onMouseEnter={(event) =>
                      openPracticeAreasMenu(event.currentTarget)
                    }
                    onMouseLeave={schedulePracticeAreasClose}
                  >
                    <DropdownButton
                      type="button"
                      active={practiceAreasOpen ? 1 : 0}
                      onClick={(event) => {
                        if (practiceAreasOpen) {
                          closePracticeAreasMenu();
                          return;
                        }

                        openPracticeAreasMenu(event.currentTarget);
                      }}
                      endIcon={
                        practiceAreasOpen ? (
                          <KeyboardArrowUpRoundedIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownRoundedIcon fontSize="small" />
                        )
                      }
                    >
                      Practice Areas
                    </DropdownButton>
                  </Box>
                </Box>

                {token ? renderAuthenticatedActions() : renderUserActions()}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <AnimatePresence>
        {practiceAreasOpen && practiceAreasAnchor && !isMobile && (
          <Popper
            open={practiceAreasOpen}
            anchorEl={practiceAreasAnchor}
            placement="bottom-start"
            sx={{ zIndex: theme.zIndex.modal + 1 }}
            modifiers={[
              {
                name: "offset",
                options: {
                  offset: [0, 18],
                },
              },
            ]}
          >
            <ClickAwayListener onClickAway={closePracticeAreasMenu}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: -16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -12, scale: 0.98 }}
                transition={dropdownTransition}
                onMouseEnter={clearPracticeAreasTimer}
                onMouseLeave={schedulePracticeAreasClose}
              >
                <Paper
                  elevation={0}
                  sx={{
                    width: "min(42rem, calc(100vw - 2rem))",
                    borderRadius: "1.5rem",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    boxShadow: `0 32px 80px ${alpha(theme.palette.common.black, 0.16)}`,
                    overflow: "hidden",
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(245,242,255,0.96) 100%)",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1.35fr 0.9fr" },
                      minHeight: { xs: "auto", md: "23rem" },
                    }}
                  >
                    <Box sx={{ p: { xs: "1.4rem", md: "1.7rem" } }}>
                      <Typography
                        variant="overline"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: "0.12rem",
                          color: theme.palette.primary.main,
                        }}
                      >
                        Practice Areas
                      </Typography>

                      <Typography
                        variant="h6"
                        sx={{ mt: "0.2rem", mb: "0.45rem", fontWeight: 700 }}
                      >
                        Explore legal workflows by specialty
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mb: "1.35rem",
                          color: theme.palette.text.secondary,
                        }}
                      >
                        Browse the practice areas your firm can operationalize
                        with a faster, cleaner client experience.
                      </Typography>

                      <Grid container spacing={1.2}>
                        {practiceAreas.map((area) => (
                          <Grid item xs={12} sm={6} key={area}>
                            <Button
                              fullWidth
                              onClick={() => handlePracticeAreaClick(area)}
                              sx={{
                                justifyContent: "flex-start",
                                textTransform: "none",
                                color: theme.palette.text.primary,
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                padding: "0.85rem 1rem",
                                borderRadius: "1rem",
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.04,
                                ),
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.08,
                                  ),
                                  color: theme.palette.primary.main,
                                  transform: "translateY(-1px)",
                                },
                              }}
                            >
                              {area}
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    <Box
                      sx={{
                        position: "relative",
                        overflow: "hidden",
                        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.common.white, 0.72)} 100%)`,
                        borderLeft: {
                          xs: "none",
                          md: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                        },
                        minHeight: { xs: "12rem", md: "100%" },
                      }}
                    >
                      <Box sx={{ position: "absolute", inset: 0, p: "1.4rem" }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            mb: "0.35rem",
                          }}
                        >
                          Built for modern firms
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          Keep the navigation light, while letting the dropdown
                          carry the richer information architecture.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          position: "absolute",
                          right: "-1.2rem",
                          bottom: "-1.8rem",
                          width: { xs: "12rem", md: "18rem" },
                          height: { xs: "12rem", md: "18rem" },
                          pointerEvents: "none",
                        }}
                      >
                        <Wormhole practiceAreas={practiceAreas} />
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </ClickAwayListener>
          </Popper>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileMenuOpen && isMobile && (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={dropdownTransition}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: theme.zIndex.modal + 2,
              backgroundColor: alpha(theme.palette.common.black, 0.3),
              height: "100dvh",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              p: "0.6rem",
            }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <Box
              component={motion.div}
              initial={{ opacity: 0, x: 40, scale: 0.98 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 40, scale: 0.98 }}
              transition={mobilePanelTransition}
              sx={{
                ml: "auto",
                width: "min(24rem, calc(100vw - 1.2rem))",
                height: "calc(100dvh - 1.2rem)",
                borderRadius: "1.5rem",
                border: `1px solid ${alpha(theme.palette.common.white, 0.32)}`,
                background: `linear-gradient(135deg, rgba(255,255,255) 0%, rgba(244,241,255) 100%)`,
                boxShadow: `0 34px 90px ${alpha(theme.palette.common.black, 0.24)}`,
                overflow: "hidden",
                backdropFilter: "blur(24px)",
                WebkitBackdropFilter: "blur(24px)",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  p: "1rem",
                  gap: "1rem",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <Box
                    sx={{ width: "8rem", height: "2.4rem", cursor: "pointer" }}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/");
                    }}
                  >
                    <img
                      src={companyLogo}
                      alt="LexMom logo"
                      style={{
                        height: "100%",
                        width: "100%",
                        objectFit: "contain",
                      }}
                      onError={(event) => {
                        event.target.src = require("../../assets/nav-lexmom.webp");
                      }}
                    />
                  </Box>

                  <IconButton
                    onClick={() => setMobileMenuOpen(false)}
                    sx={{
                      color: theme.palette.text.primary,
                      width: "2.8rem",
                      height: "2.8rem",
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ px: "0.1rem" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, mb: "0.2rem" }}
                  >
                    Navigation
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Clear, mobile-friendly access to the key landing page
                    routes.
                  </Typography>
                </Box>

                <Divider
                  sx={{ borderColor: alpha(theme.palette.primary.main, 0.08) }}
                />

                <Box
                  component={motion.div}
                  variants={mobileListVariants}
                  initial="hidden"
                  animate="visible"
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    pr: "0.15rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                  }}
                >
                  {navItems.map((item) => (
                    <Box
                      key={item.id}
                      component={motion.div}
                      variants={mobileItemVariants}
                    >
                      <ListItemButton
                        onClick={() => handleLinkClick(item)}
                        sx={{
                          borderRadius: "1rem",
                          px: "0.95rem",
                          py: "0.9rem",
                          border: `1px solid ${activeLink === item.id ? alpha(theme.palette.primary.main, 0.18) : alpha(theme.palette.primary.main, 0.08)}`,
                          backgroundColor:
                            activeLink === item.id
                              ? alpha(theme.palette.primary.main, 0.1)
                              : alpha(theme.palette.primary.main, 0.03),
                        }}
                      >
                        <ListItemText
                          primary={item.label}
                          // secondary={
                          //   item.type === "scroll"
                          //     ? "Jumps to a landing-page section"
                          //     : "Opens a dedicated page"
                          // }
                          primaryTypographyProps={{
                            fontWeight: activeLink === item.id ? 700 : 600,
                            fontSize: "0.95rem",
                            color:
                              activeLink === item.id
                                ? theme.palette.primary.main
                                : theme.palette.text.primary,
                          }}
                          secondaryTypographyProps={{
                            fontSize: "0.76rem",
                            color: theme.palette.text.secondary,
                          }}
                        />
                      </ListItemButton>
                    </Box>
                  ))}

                  <Box component={motion.div} variants={mobileItemVariants}>
                    <Box
                      sx={{
                        borderRadius: "1rem",
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.04,
                        ),
                        overflow: "hidden",
                      }}
                    >
                      <ListItemButton
                        onClick={() =>
                          setMobilePracticeAreasOpen(
                            (previousState) => !previousState,
                          )
                        }
                        sx={{ px: "0.95rem", py: "0.95rem" }}
                      >
                        <ListItemText
                          primary="Practice Areas"
                          secondary="Expanded in-place with motion"
                          primaryTypographyProps={{
                            fontWeight: 700,
                            fontSize: "0.95rem",
                          }}
                          secondaryTypographyProps={{
                            fontSize: "0.76rem",
                            color: theme.palette.text.secondary,
                          }}
                        />
                        {mobilePracticeAreasOpen ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </ListItemButton>

                      <AnimatePresence initial={false}>
                        {mobilePracticeAreasOpen && (
                          <Box
                            component={motion.div}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={dropdownTransition}
                            sx={{
                              overflow: "hidden",
                              px: "0.8rem",
                              pb: "0.8rem",
                            }}
                          >
                            {renderPracticeAreasGrid(true)}
                          </Box>
                        )}
                      </AnimatePresence>
                    </Box>
                  </Box>
                </Box>

                <Divider
                  sx={{ borderColor: alpha(theme.palette.primary.main, 0.08) }}
                />
                <Box sx={{ flexShrink: 0 }}>
                  {token
                    ? renderAuthenticatedActions(true)
                    : renderUserActions(true)}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
